import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Plus, Edit2, Trash2, CheckCircle, Wind, Activity, Heart, 
  Calendar, Clock, FileText
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { 
  usePatientCustomExercisePlans,
  useCreateCustomExercisePlan,
  useUpdateCustomExercisePlan,
  useDeleteExercisePlan
} from '../lib/hooks/useExercises';
import { useAuth } from '../lib/hooks/useAuth';

// Dumbbell icon component (lucide-react doesn't have it, so we'll use a simple SVG)
const Dumbbell = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
  >
    <path d="M14.4 14.4 9.6 9.6" />
    <path d="M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.829l-1.768 1.767a2 2 0 1 1 2.828 2.829z" />
    <path d="m21.5 21.5-1.4-1.4" />
    <path d="M3.9 3.9 2.5 2.5" />
    <path d="M6.404 12.768a2 2 0 1 1-2.829-2.829l1.768-1.767a2 2 0 1 1-2.828-2.829l2.828-2.828a2 2 0 1 1 2.829 2.828l1.767-1.768a2 2 0 1 1 2.829 2.829z" />
  </svg>
);

interface ExercisePlanManagementProps {
  patientName: string;
  patientId: string;
  onBack: () => void;
  onSave?: (plan: ExercisePlan) => void;
}

interface ExerciseItem {
  id: string;
  type: 'Breathing' | 'Stretching' | 'Cardio' | 'Strength';
  duration: number;
  intensity: 'Low' | 'Moderate' | 'High';
  frequency: number;
  progressiveLoad: string;
}

interface ExercisePlan {
  id: string;
  patientId: string;
  patientName: string;
  exercises: ExerciseItem[];
  createdDate: string;
  lastModified: string;
  status: 'draft' | 'active';
}

export function ExercisePlanManagement({ patientName, patientId, onBack, onSave }: ExercisePlanManagementProps) {
  // Get auth user
  const { user } = useAuth();
  const therapistId = user?.therapist_id || 0;
  
  // Fetch existing custom plans from API
  const { data: plansData, isLoading } = usePatientCustomExercisePlans(parseInt(patientId), false);
  
  // Mutations for backend
  const createPlanMutation = useCreateCustomExercisePlan();
  const updatePlanMutation = useUpdateCustomExercisePlan();
  const deletePlanMutation = useDeleteExercisePlan();
  
  const [existingPlans, setExistingPlans] = useState<ExercisePlan[]>([]);
  
  // Update existing plans when API data loads
  useEffect(() => {
    if (plansData?.exercise_plans) {
      // Transform API data to local format
      const transformedPlans = plansData.exercise_plans.map((plan: any) => ({
        id: plan.id?.toString() || '',
        patientId: plan.patient_id?.toString() || patientId,
        patientName: patientName,
        status: plan.status || 'active',
        createdDate: plan.created_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        lastModified: plan.last_modified?.split('T')[0] || new Date().toISOString().split('T')[0],
        exercises: plan.exercises || []
      }));
      setExistingPlans(transformedPlans);
    }
  }, [plansData, patientId, patientName]);

  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [exercises, setExercises] = useState<ExerciseItem[]>([]);
  const [currentExercise, setCurrentExercise] = useState<Partial<ExerciseItem>>({
    duration: 15,
    intensity: 'Moderate',
    frequency: 3,
    progressiveLoad: '+10% every 2 weeks'
  });
  const [activeTab, setActiveTab] = useState<'existing' | 'create'>('existing');

  const exerciseTypes = [
    { value: 'Breathing', label: 'Breathing Exercises', icon: Wind, color: '#14CAF4' },
    { value: 'Stretching', label: 'Stretching Exercises', icon: Activity, color: '#F07D13' },
    { value: 'Cardio', label: 'Cardio Training', icon: Heart, color: '#2762AC' },
    { value: 'Strength', label: 'Strength Building', icon: Dumbbell, color: '#494949' }
  ];

  const intensityLevels = [
    { value: 'Low', label: 'Low Intensity', description: 'Gentle, comfortable pace' },
    { value: 'Moderate', label: 'Moderate Intensity', description: 'Slightly challenging' },
    { value: 'High', label: 'High Intensity', description: 'Maximum effort' }
  ];

  const progressiveLoadOptions = [
    '+10% every 2 weeks',
    '+15% every 2 weeks',
    '+10% every week',
    '+5% every week',
    'Custom progression'
  ];

  const getExerciseIcon = (type: string) => {
    const exercise = exerciseTypes.find(e => e.value === type);
    return exercise?.icon || Activity;
  };

  const getExerciseColor = (type: string) => {
    const exercise = exerciseTypes.find(e => e.value === type);
    return exercise?.color || '#494949';
  };

  const getIntensityColor = (intensity: string) => {
    switch (intensity) {
      case 'Low':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      case 'Moderate':
        return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
      case 'High':
        return 'bg-destructive/10 text-destructive border-destructive';
      default:
        return '';
    }
  };

  const handleEditPlan = (plan: ExercisePlan) => {
    setEditingPlanId(plan.id);
    setExercises([...plan.exercises]);
    setActiveTab('create');
    toast.info('Editing plan', {
      description: `Now editing ${plan.status} plan from ${new Date(plan.createdDate).toLocaleDateString()}`
    });
  };

  const handleDeletePlan = (planId: string) => {
    // Call mutation with correct parameters
    deletePlanMutation.mutate({ 
      planId: parseInt(planId), 
      patientId: parseInt(patientId) 
    });
    
    // Optimistically update local state
    setExistingPlans(existingPlans.filter(p => p.id !== planId));
    
    toast.success('Plan deleted', {
      description: 'Exercise plan has been removed'
    });
  };

  const handleSelectExerciseType = (type: 'Breathing' | 'Stretching' | 'Cardio' | 'Strength') => {
    setCurrentExercise({
      type,
      duration: 15,
      intensity: 'Moderate',
      frequency: 3,
      progressiveLoad: '+10% every 2 weeks'
    });
    toast.success(`${type} exercise selected`, {
      description: 'Fill in the details below'
    });
  };

  const handleAddExercise = () => {
    if (!currentExercise.type || !currentExercise.duration || !currentExercise.intensity || 
        !currentExercise.frequency || !currentExercise.progressiveLoad) {
      toast.error('Please fill in all fields');
      return;
    }

    const newExercise: ExerciseItem = {
      id: `ex-${Date.now()}`,
      type: currentExercise.type as any,
      duration: currentExercise.duration,
      intensity: currentExercise.intensity as any,
      frequency: currentExercise.frequency,
      progressiveLoad: currentExercise.progressiveLoad
    };

    setExercises([...exercises, newExercise]);
    
    // Reset form
    setCurrentExercise({
      duration: 15,
      intensity: 'Moderate',
      frequency: 3,
      progressiveLoad: '+10% every 2 weeks'
    });

    toast.success('Exercise added to plan', {
      description: `${newExercise.type} exercise added successfully`
    });
  };

  const handleRemoveExercise = (id: string) => {
    setExercises(exercises.filter(ex => ex.id !== id));
    toast.info('Exercise removed from plan');
  };

  const handleSavePlan = (status: 'draft' | 'active') => {
    if (exercises.length === 0) {
      toast.error('Please add at least one exercise to the plan');
      return;
    }

    const now = new Date().toISOString();

    if (editingPlanId) {
      // Update existing custom plan
      updatePlanMutation.mutate({ 
        planId: parseInt(editingPlanId),
        data: {
          plan_title: `${patientName}'s Exercise Plan`,
          exercises: exercises,
          status: status,
          notes: `Updated plan with ${exercises.length} exercises`
        }
      });
      
      // Optimistically update local state
      const updatedPlans = existingPlans.map(p => 
        p.id === editingPlanId 
          ? {
              ...p,
              exercises,
              lastModified: now,
              status
            }
          : p
      );
      setExistingPlans(updatedPlans);
      
      toast.success('Plan updated successfully!', {
        description: `Exercise plan has been ${status === 'active' ? 'activated' : 'saved as draft'}`
      });
      
      setEditingPlanId(null);
      setExercises([]);
      setActiveTab('existing');
    } else {
      // Create new custom plan
      const planData = {
        patient_id: parseInt(patientId),
        plan_title: `${patientName}'s Exercise Plan - ${new Date().toLocaleDateString()}`,
        exercises: exercises,
        status: status,
        notes: `Plan with ${exercises.length} exercises: ${exercises.map(e => e.type).join(', ')}`
      };

      createPlanMutation.mutate(planData);
      
      // Optimistically create plan for local display
      const plan: ExercisePlan = {
        id: `plan-${Date.now()}`,
        patientId,
        patientName,
        exercises,
        createdDate: now,
        lastModified: now,
        status
      };
      setExistingPlans([plan, ...existingPlans]);

      if (onSave) {
        onSave(plan);
      }

      toast.success(
        status === 'active' ? 'Exercise plan activated!' : 'Exercise plan saved as draft',
        {
          description: `${exercises.length} exercise(s) ${status === 'active' ? 'assigned to' : 'saved for'} ${patientName}`
        }
      );

      setExercises([]);
      setActiveTab('existing');
    }
  };

  const handleCancelEdit = () => {
    setEditingPlanId(null);
    setExercises([]);
    setCurrentExercise({
      duration: 15,
      intensity: 'Moderate',
      frequency: 3,
      progressiveLoad: '+10% every 2 weeks'
    });
    setActiveTab('existing');
    toast.info('Edit cancelled');
  };

  const totalWeeklyMinutes = exercises.reduce((sum, ex) => sum + (ex.duration * ex.frequency), 0);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <Button
            variant="ghost"
            onClick={onBack}
            className="mb-3 sm:mb-4 -ml-2 text-xs sm:text-sm h-8 sm:h-9"
          >
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Back to Patient List
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4">
            <div className="min-w-0">
              <h1 className="text-primary text-lg sm:text-xl md:text-2xl">Exercise Plan Management</h1>
              <p className="text-muted-foreground text-xs sm:text-sm truncate">Managing rehabilitation programs for {patientName}</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="existing" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Existing Plans</span>
              <span className="sm:hidden">Plans</span>
              {existingPlans.length > 0 && (
                <Badge variant="outline" className="ml-1 bg-primary/10 text-primary border-primary">
                  {existingPlans.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="create" className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{editingPlanId ? 'Edit Plan' : 'Create New'}</span>
              <span className="sm:hidden">{editingPlanId ? 'Edit' : 'New'}</span>
            </TabsTrigger>
          </TabsList>

          {/* Existing Plans Tab */}
          <TabsContent value="existing" className="space-y-4">
            {existingPlans.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="p-12 text-center">
                  <Dumbbell className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-foreground text-lg mb-2">No Exercise Plans Yet</h3>
                  <p className="text-muted-foreground mb-6">
                    Create a personalized exercise plan for {patientName}
                  </p>
                  <Button 
                    className="bg-[#14CAF4] hover:bg-[#14CAF4]/90"
                    onClick={() => setActiveTab('create')}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Plan
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {existingPlans.map((plan) => (
                  <Card key={plan.id} className="shadow-lg">
                    <CardHeader className="bg-[#D4F0F8] pb-3">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <CardTitle className="text-primary text-base sm:text-lg">
                              Exercise Plan
                            </CardTitle>
                            <Badge 
                              variant="outline" 
                              className={plan.status === 'active' 
                                ? 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]' 
                                : 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]'}
                            >
                              {plan.status === 'active' ? 'Active' : 'Draft'}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>Created: {new Date(plan.createdDate).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>Modified: {new Date(plan.lastModified).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditPlan(plan)}
                            className="border-primary text-primary hover:bg-primary/10"
                          >
                            <Edit2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Edit</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeletePlan(plan.id)}
                            className="border-destructive text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Delete</span>
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm text-muted-foreground">
                            {plan.exercises.length} Exercise{plan.exercises.length !== 1 ? 's' : ''}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Total: {plan.exercises.reduce((sum, ex) => sum + (ex.duration * ex.frequency), 0)} min/week
                          </p>
                        </div>
                        {plan.exercises.map((exercise) => {
                          const Icon = getExerciseIcon(exercise.type);
                          return (
                            <div
                              key={exercise.id}
                              className="p-3 sm:p-4 border border-border rounded-xl bg-secondary/50"
                            >
                              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <div
                                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: `${getExerciseColor(exercise.type)}20` }}
                                >
                                  <Icon 
                                    className="w-5 h-5" 
                                    style={{ color: getExerciseColor(exercise.type) }}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm sm:text-base truncate">{exercise.type} Exercise</p>
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1">
                                    <Badge variant="outline" className={`${getIntensityColor(exercise.intensity)} text-xs w-fit`}>
                                      {exercise.intensity}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">
                                      {exercise.duration} min • {exercise.frequency}x/week • {exercise.progressiveLoad}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Create/Edit Plan Tab */}
          <TabsContent value="create" className="space-y-6">
            {editingPlanId && (
              <Card className="shadow-lg border-l-4 border-l-accent">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm">Editing existing plan</p>
                      <p className="text-xs text-muted-foreground">Make changes and save to update</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                    >
                      Cancel Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-2 mb-4">
              <Button
                variant="outline"
                onClick={() => handleSavePlan('draft')}
                disabled={exercises.length === 0}
                size="sm"
                className="text-xs sm:text-sm"
              >
                Save as Draft
              </Button>
              <Button
                className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-xs sm:text-sm"
                onClick={() => handleSavePlan('active')}
                disabled={exercises.length === 0}
                size="sm"
              >
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                {editingPlanId ? 'Update Plan' : 'Activate Plan'}
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Exercise Plan Form - Left Side */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                <Card className="shadow-lg">
                  <CardHeader className="bg-[#D4F0F8] p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <CardTitle className="text-primary flex items-center gap-2 text-base sm:text-lg">
                        <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                        Add Exercise to Plan
                      </CardTitle>
                      {currentExercise.type && (
                        <Badge variant="outline" className="bg-primary/10 text-primary border-primary text-xs whitespace-nowrap">
                          {currentExercise.type} Selected
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
                    {/* Exercise Type Selection */}
                    <div className="space-y-2 sm:space-y-3">
                      <Label className="text-primary text-sm sm:text-base">🔹 Select Exercise Type</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {exerciseTypes.map((type) => {
                          const Icon = type.icon;
                          return (
                            <button
                              key={type.value}
                              onClick={() => handleSelectExerciseType(type.value as any)}
                              className={`p-3 sm:p-4 border-2 rounded-xl transition-all hover:shadow-md text-left ${
                                currentExercise.type === type.value
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/50'
                              }`}
                            >
                              <div className="flex items-center gap-2 sm:gap-3">
                                <div
                                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: `${type.color}20` }}
                                >
                                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: type.color }} />
                                </div>
                                <span className="text-xs sm:text-sm truncate">{type.label}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {currentExercise.type && (
                      <>
                        {/* Duration */}
                        <div className="space-y-2">
                          <Label htmlFor="duration" className="text-primary text-sm sm:text-base">
                            🔹 Duration (minutes)
                          </Label>
                          <Input
                            id="duration"
                            type="number"
                            min="5"
                            max="120"
                            value={currentExercise.duration}
                            onChange={(e) => setCurrentExercise({ ...currentExercise, duration: parseInt(e.target.value) })}
                            className="text-sm sm:text-base"
                          />
                        </div>

                        {/* Intensity */}
                        <div className="space-y-2 sm:space-y-3">
                          <Label className="text-primary text-sm sm:text-base">🔹 Intensity Level</Label>
                          <div className="grid grid-cols-1 gap-2 sm:gap-3">
                            {intensityLevels.map((level) => (
                              <button
                                key={level.value}
                                onClick={() => setCurrentExercise({ ...currentExercise, intensity: level.value as any })}
                                className={`p-3 sm:p-4 border-2 rounded-xl transition-all hover:shadow-md text-left ${
                                  currentExercise.intensity === level.value
                                    ? 'border-primary bg-primary/5'
                                    : 'border-border hover:border-primary/50'
                                }`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div>
                                    <p className="text-xs sm:text-sm">{level.label}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">{level.description}</p>
                                  </div>
                                  <Badge variant="outline" className={getIntensityColor(level.value)}>
                                    {level.value}
                                  </Badge>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Frequency */}
                        <div className="space-y-2">
                          <Label htmlFor="frequency" className="text-primary text-sm sm:text-base">
                            🔹 Frequency (times per week)
                          </Label>
                          <Input
                            id="frequency"
                            type="number"
                            min="1"
                            max="7"
                            value={currentExercise.frequency}
                            onChange={(e) => setCurrentExercise({ ...currentExercise, frequency: parseInt(e.target.value) })}
                            className="text-sm sm:text-base"
                          />
                        </div>

                        {/* Progressive Load */}
                        <div className="space-y-2">
                          <Label htmlFor="progressive-load" className="text-primary text-sm sm:text-base">
                            🔹 Progressive Load
                          </Label>
                          <Select
                            value={currentExercise.progressiveLoad}
                            onValueChange={(value) => setCurrentExercise({ ...currentExercise, progressiveLoad: value })}
                          >
                            <SelectTrigger id="progressive-load" className="text-sm sm:text-base">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {progressiveLoadOptions.map((option) => (
                                <SelectItem key={option} value={option} className="text-sm sm:text-base">
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <Button
                          onClick={handleAddExercise}
                          className="w-full bg-accent hover:bg-accent/90 text-sm sm:text-base"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Exercise to Plan
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Plan Summary - Right Side */}
              <div className="lg:col-span-1">
                <Card className="shadow-lg sticky top-4">
                  <CardHeader className="bg-primary text-primary-foreground p-4 sm:p-6">
                    <CardTitle className="text-base sm:text-lg">Plan Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 space-y-4">
                    <div className="text-center py-4 border-b border-border">
                      <p className="text-xs sm:text-sm text-muted-foreground mb-1">Total Exercises</p>
                      <p className="text-2xl sm:text-3xl text-primary">{exercises.length}</p>
                    </div>

                    <div className="text-center py-4 border-b border-border">
                      <p className="text-xs sm:text-sm text-muted-foreground mb-1">Weekly Minutes</p>
                      <p className="text-2xl sm:text-3xl text-accent">{totalWeeklyMinutes}</p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs sm:text-sm text-muted-foreground">Exercises in Plan:</p>
                      {exercises.length === 0 ? (
                        <p className="text-xs sm:text-sm text-muted-foreground italic text-center py-4">
                          No exercises added yet
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {exercises.map((exercise) => {
                            const Icon = getExerciseIcon(exercise.type);
                            return (
                              <div
                                key={exercise.id}
                                className="p-2 sm:p-3 bg-secondary rounded-lg flex items-center justify-between gap-2"
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <Icon
                                    className="w-4 h-4 flex-shrink-0"
                                    style={{ color: getExerciseColor(exercise.type) }}
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs sm:text-sm truncate">{exercise.type}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {exercise.duration}min • {exercise.frequency}x/wk
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRemoveExercise(exercise.id)}
                                  className="h-6 w-6 p-0 flex-shrink-0"
                                >
                                  <Trash2 className="w-3 h-3 text-destructive" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}